import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Clock, Gavel, XCircle } from 'lucide-react';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Skeleton, SkeletonRegion } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { formatarDataHora, formatarDistancia } from '@/lib/format';
import { useAuthStore } from '@/features/auth/auth.store';
import { NomeDePessoa, useDicionarioDePessoas } from '@/features/tickets/pessoas';
import {
  chavesDeAprovacao,
  decidirAprovacao,
  listarAprovacoesDoChamado,
  obterAprovacao,
  ROTULO_DE_APROVACAO,
  ROTULO_DE_ETAPA,
  type Aprovacao,
  type EtapaResolvida,
} from '@/features/workflow/aprovacoes.api';
import { AvisoDeFalha, Chave, CLASSE_DE_CAMPO } from '@/features/workflow/componentes';
import { classificarFalha, type Falha } from '@/features/workflow/recusa';
import { chaves } from '@/features/tickets/tickets.api';

/**
 * As aprovações de uma entidade, e o botão de decidir.
 *
 * ★ É **a mesma** máquina da Fase 3, sem nenhuma cópia: o Comitê de Mudanças
 * de uma mudança normal, o Comitê Emergencial de uma emergencial e a exceção
 * de congelamento chegam aqui pelo mesmo `ApprovalRequest`. Uma segunda
 * máquina de aprovação seria a que ninguém audita.
 *
 * ## O que a tela mostra, e por quê
 *
 *   • **a política pela chave** (`cab-full`, `ecab`, `blackout-exception`) —
 *     porque é ela que explica por que são três assinaturas e não uma, e é o
 *     que quem administra o processo reconhece;
 *   • **quórum satisfeito × exigido**, por etapa, em vez de uma barra genérica:
 *     "2 de 3 do CAB" é a frase que a reunião usa;
 *   • **quem decidiu, quando e com que comentário** — a trilha do critério 3.6.
 *
 * Quem pode decidir vê o formulário; quem não pode lê por que a decisão não é
 * dele. A razão dessa escolha está em `DecisaoDaEtapa`, logo abaixo.
 */

/** As aprovações de um chamado — CAB e ECAB entram por aqui. */
export function AprovacoesDoChamado({ ticketId }: { ticketId: string }) {
  const consulta = useQuery({
    queryKey: chavesDeAprovacao.doChamado(ticketId),
    queryFn: () => listarAprovacoesDoChamado(ticketId),
  });

  if (consulta.isPending) {
    return (
      <SkeletonRegion label="Carregando aprovações">
        <Skeleton className="h-24 w-full" />
      </SkeletonRegion>
    );
  }
  if (consulta.isError) {
    return (
      <AvisoDeFalha
        falha={classificarFalha(consulta.error)}
        aoRecarregar={() => void consulta.refetch()}
      />
    );
  }
  if (consulta.data.length === 0) {
    return (
      <EmptyState
        icon={<Gavel className="h-6 w-6" aria-hidden />}
        title="Nenhuma aprovação aberta para este chamado"
        description="A aprovação nasce de um efeito do fluxo: é a transição que pede o comitê, e não um botão desta tela. Enquanto o chamado não chegar ao estado que a pede, não há o que decidir."
      />
    );
  }

  return (
    <div className="space-y-4">
      {consulta.data.map((aprovacao) => (
        <CartaoDeAprovacao key={aprovacao.id} aprovacao={aprovacao} ticketId={ticketId} />
      ))}
    </div>
  );
}

/**
 * Uma aprovação carregada pelo identificador.
 *
 * É o caminho da exceção de congelamento: ela é aberta com
 * `entityType: 'ChangeBlackoutException'`, então não aparece em
 * `GET /tickets/:id/approvals` — mas a própria exceção guarda o
 * `approvalRequestId`, e é com ele que a tela chega aqui.
 */
export function AprovacaoPorId({
  approvalRequestId,
  ticketId,
}: {
  approvalRequestId: string;
  ticketId?: string;
}) {
  const consulta = useQuery({
    queryKey: chavesDeAprovacao.aprovacao(approvalRequestId),
    queryFn: () => obterAprovacao(approvalRequestId),
  });

  if (consulta.isPending) return <Skeleton className="h-24 w-full" />;
  if (consulta.isError) {
    return (
      <AvisoDeFalha
        falha={classificarFalha(consulta.error)}
        aoRecarregar={() => void consulta.refetch()}
      />
    );
  }
  return <CartaoDeAprovacao aprovacao={consulta.data} {...(ticketId ? { ticketId } : {})} />;
}

// ── cartão ─────────────────────────────────────────────────────────────────

const ESTILO_DE_SITUACAO: Record<Aprovacao['status'], string> = {
  PENDING: 'border-warning/30 bg-warning/5',
  APPROVED: 'border-success/30 bg-success/5',
  REJECTED: 'border-destructive/30 bg-destructive/5',
  CANCELLED: 'border-border bg-muted/40',
  EXPIRED: 'border-destructive/30 bg-destructive/5',
};

export function CartaoDeAprovacao({
  aprovacao,
  ticketId,
}: {
  aprovacao: Aprovacao;
  ticketId?: string;
}) {
  const pessoas = useDicionarioDePessoas([
    aprovacao.requesterId,
    ...aprovacao.stages.flatMap((etapa) => etapa.steps.map((passo) => passo.resolvedUserId)),
    ...aprovacao.decisions.map((decisao) => decisao.decidedById),
  ]);

  const vencida =
    aprovacao.dueAt !== null &&
    aprovacao.status === 'PENDING' &&
    new Date(aprovacao.dueAt) < new Date();

  return (
    <div className={cn('space-y-3 rounded-lg border px-4 py-3', ESTILO_DE_SITUACAO[aprovacao.status])}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold">{ROTULO_DE_APROVACAO[aprovacao.status]}</span>
          <Chave valor={aprovacao.policyKey} titulo="Chave da política de aprovação" />
          <span className="text-xs text-muted-foreground">
            {aprovacao.mode === 'SEQUENTIAL' ? 'etapas em sequência' : 'etapas em paralelo'}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span title={formatarDataHora(aprovacao.openedAt)}>
            aberta {formatarDistancia(aprovacao.openedAt)}
          </span>
          {aprovacao.dueAt ? (
            <span
              className={cn('inline-flex items-center gap-1', vencida && 'font-semibold text-destructive')}
              title={formatarDataHora(aprovacao.dueAt)}
            >
              <Clock className="h-3 w-3" aria-hidden />
              prazo {formatarDistancia(aprovacao.dueAt)}
            </span>
          ) : null}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Pedida por <NomeDePessoa id={aprovacao.requesterId} pessoas={pessoas} /> · sobre a versão{' '}
        <span className="tabular-nums">{aprovacao.entityVersion}</span> do registro
        {aprovacao.forbidSelfApproval ? ' · autoaprovação proibida' : ''}
      </p>

      <ul className="space-y-2">
        {[...aprovacao.stages]
          .sort((a, b) => a.order - b.order)
          .map((etapa) => (
            <Etapa
              key={etapa.key}
              etapa={etapa}
              aprovacao={aprovacao}
              pessoas={pessoas}
              {...(ticketId ? { ticketId } : {})}
            />
          ))}
      </ul>

      {aprovacao.closureReason ? (
        <p className="text-xs text-muted-foreground">Encerrada: {aprovacao.closureReason}</p>
      ) : null}
    </div>
  );
}

function Etapa({
  etapa,
  aprovacao,
  pessoas,
  ticketId,
}: {
  etapa: EtapaResolvida;
  aprovacao: Aprovacao;
  pessoas: ReturnType<typeof useDicionarioDePessoas>;
  ticketId?: string;
}) {
  const desfecho =
    aprovacao.snapshot.stages.find((linha) => linha.key === etapa.key)?.outcome ?? 'PENDING';
  const ativa = aprovacao.snapshot.activeStageKeys.includes(etapa.key);
  const decisoes = aprovacao.decisions.filter((decisao) => decisao.stageKey === etapa.key);
  const aprovadas = new Set(
    decisoes.filter((d) => d.decision === 'APPROVED').map((d) => d.decidedById),
  ).size;

  return (
    <li className="space-y-1.5 rounded-md border border-border bg-card px-3 py-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium">
          {etapa.order}. {etapa.key}
        </span>
        <span
          className={cn(
            'text-xs font-semibold',
            desfecho === 'APPROVED' && 'text-success',
            desfecho === 'REJECTED' && 'text-destructive',
            desfecho === 'PENDING' && 'text-warning',
            desfecho === 'SKIPPED' && 'text-muted-foreground',
          )}
        >
          {ROTULO_DE_ETAPA[desfecho]}
        </span>
        <span className="text-xs tabular-nums text-muted-foreground">
          {aprovadas} de {exigidas(etapa)} · {descreverRegra(etapa)}
        </span>
      </div>

      {etapa.skipped && etapa.skipReason ? (
        <p className="text-xs text-muted-foreground">Dispensada: {etapa.skipReason}</p>
      ) : null}

      <ul className="space-y-1 text-xs">
        {etapa.steps.map((passo) => {
          const decisao = decisoes.find((d) => d.stepKey === passo.key);
          return (
            <li key={passo.key} className="flex flex-wrap items-center gap-2">
              {decisao ? (
                decisao.decision === 'APPROVED' ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-hidden />
                ) : (
                  <XCircle className="h-3.5 w-3.5 text-destructive" aria-hidden />
                )
              ) : (
                <Clock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
              )}
              <NomeDePessoa id={passo.resolvedUserId} pessoas={pessoas} />
              {decisao ? (
                <>
                  <span className="text-muted-foreground" title={formatarDataHora(decisao.decidedAt)}>
                    {decisao.decision === 'APPROVED' ? 'aprovou' : 'reprovou'}{' '}
                    {formatarDistancia(decisao.decidedAt)}
                  </span>
                  {decisao.comment ? (
                    <span className="text-muted-foreground">— “{decisao.comment}”</span>
                  ) : null}
                </>
              ) : (
                <span className="text-muted-foreground">ainda não decidiu</span>
              )}
            </li>
          );
        })}
      </ul>

      {ativa && aprovacao.status === 'PENDING' ? (
        <DecisaoDaEtapa etapa={etapa} aprovacao={aprovacao} {...(ticketId ? { ticketId } : {})} />
      ) : null}
    </li>
  );
}

/**
 * O formulário de decisão só aparece para quem **pode** decidir.
 *
 * É a exceção deliberada à regra de "mostrar apagado com o motivo": aqui a
 * ação não está bloqueada por uma condição que a pessoa possa resolver — ela
 * simplesmente não é dela. Um botão que responde 403 a todo mundo que não é o
 * aprovador ensinaria a clicar e ignorar a recusa, que é o pior hábito
 * possível numa tela de aprovação.
 *
 * Quem decide sai do **aprovador congelado na abertura** (`resolvedUserId`) e
 * da delegação — as duas coisas que o servidor confere. Trocar o gestor de
 * alguém amanhã não muda quem tinha o direito hoje, e é por isso que a conta
 * não é feita sobre o papel corrente.
 */
function DecisaoDaEtapa({
  etapa,
  aprovacao,
  ticketId,
}: {
  etapa: EtapaResolvida;
  aprovacao: Aprovacao;
  ticketId?: string;
}) {
  const usuario = useAuthStore((estado) => estado.user);

  const pendentes = etapa.steps
    .map((passo) => passo.key)
    .filter((chave) => aprovacao.snapshot.pendingStepKeys.includes(chave));

  const meus = pendentes.filter((chave) => {
    const passo = etapa.steps.find((item) => item.key === chave);
    if (passo === undefined || usuario === null) return false;
    if (passo.resolvedUserId === usuario.id) return true;
    return (aprovacao.delegations ?? []).some(
      (delegacao) => delegacao.stepKey === chave && delegacao.toUserId === usuario.id,
    );
  });

  if (pendentes.length === 0) return null;

  if (meus.length === 0) {
    return (
      <p className="border-t border-border pt-2 text-xs text-muted-foreground">
        A decisão desta etapa não é sua — ela espera{' '}
        {pendentes.length === 1 ? 'a pessoa acima' : `${pendentes.length} das pessoas acima`}.
      </p>
    );
  }

  return (
    <FormularioDeDecisao
      aprovacao={aprovacao}
      passos={meus}
      {...(ticketId ? { ticketId } : {})}
    />
  );
}

function exigidas(etapa: EtapaResolvida): number {
  const total = new Set(etapa.steps.map((passo) => passo.resolvedUserId)).size;
  switch (etapa.rule.kind) {
    case 'ANY':
      return 1;
    case 'ALL':
      return total;
    case 'QUORUM_N':
      return Math.max(1, etapa.rule.n ?? 1);
    case 'PERCENT':
      return Math.max(1, Math.ceil((total * (etapa.rule.percent ?? 100)) / 100));
  }
}

function descreverRegra(etapa: EtapaResolvida): string {
  switch (etapa.rule.kind) {
    case 'ANY':
      return 'basta uma aprovação';
    case 'ALL':
      return 'exige todas';
    case 'QUORUM_N':
      return `quórum de ${etapa.rule.n ?? 1}`;
    case 'PERCENT':
      return `${etapa.rule.percent ?? 100}% do comitê`;
  }
}

/**
 * Decidir.
 *
 * O passo é escolhido explicitamente quando a etapa tem mais de um: numa
 * política de quórum, dizer "aprovo" sem dizer *como quem* deixaria o servidor
 * adivinhar — e o motor recusa exatamente isso.
 */
function FormularioDeDecisao({
  aprovacao,
  passos,
  ticketId,
}: {
  aprovacao: Aprovacao;
  passos: readonly string[];
  ticketId?: string;
}) {
  const cliente = useQueryClient();
  const [escolhido, setEscolhido] = useState(passos[0] ?? '');
  const [comentario, setComentario] = useState('');
  const [falha, setFalha] = useState<Falha | null>(null);

  /**
   * O passo enviado é sempre um dos que **ainda** estão pendentes.
   *
   * `useState(passos[0])` semeia uma vez, e a lista de pendentes encolhe a cada
   * decisão — inclusive as de outras pessoas, que chegam pela consulta refeita.
   * Sem esta linha o `<select>` fica com um `value` que não existe entre as
   * `<option>` (o navegador mostra a primeira, e a tela mente sobre o que vai
   * enviar) e o clique reenvia um passo já decidido, que o motor recusa.
   */
  const passo = passos.includes(escolhido) ? escolhido : (passos[0] ?? '');

  const decidir = useMutation({
    mutationFn: (decision: 'APPROVED' | 'REJECTED') =>
      decidirAprovacao(aprovacao.id, {
        stepKey: passo,
        decision,
        ...(comentario.trim() ? { comment: comentario.trim() } : {}),
      }),
    onMutate: () => setFalha(null),
    onSuccess: () => {
      setComentario('');
      // A decisão que **fecha** a aprovação dispara `ApprovalCompleted`, e o
      // fluxo pode mover o chamado por evento. Invalidar só a aprovação
      // deixaria o painel de transições mostrando o estado anterior.
      void cliente.invalidateQueries({ queryKey: chavesDeAprovacao.raiz });
      void cliente.invalidateQueries({ queryKey: chaves.raiz });
      if (ticketId !== undefined) {
        void cliente.invalidateQueries({ queryKey: chavesDeAprovacao.doChamado(ticketId) });
      }
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  if (passos.length === 0) return null;

  return (
    <div className="space-y-2 border-t border-border pt-2">
      {passos.length > 1 ? (
        <select
          value={passo}
          onChange={(evento) => setEscolhido(evento.target.value)}
          aria-label="Passo a decidir"
          className={`${CLASSE_DE_CAMPO} max-w-xs`}
        >
          {passos.map((chave) => (
            <option key={chave} value={chave}>
              {chave}
            </option>
          ))}
        </select>
      ) : null}

      <textarea
        rows={2}
        value={comentario}
        onChange={(evento) => setComentario(evento.target.value)}
        aria-label="Comentário da decisão"
        placeholder="Comentário — fica na trilha, junto com quem decidiu, quando e de qual endereço."
        className={CLASSE_DE_CAMPO}
      />

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          loading={decidir.isPending}
          onClick={() => decidir.mutate('APPROVED')}
          icon={<CheckCircle2 className="h-4 w-4" aria-hidden />}
        >
          Aprovar
        </Button>
        <Button
          size="sm"
          variant="danger"
          loading={decidir.isPending}
          onClick={() => decidir.mutate('REJECTED')}
          icon={<XCircle className="h-4 w-4" aria-hidden />}
        >
          Reprovar
        </Button>
      </div>

      {falha ? (
        <AvisoDeFalha
          falha={falha}
          aoRecarregar={() => {
            void cliente.refetchQueries({ queryKey: chavesDeAprovacao.raiz });
            if (ticketId !== undefined) {
              void cliente.refetchQueries({ queryKey: chaves.item(ticketId) });
            }
          }}
        />
      ) : null}
    </div>
  );
}
